import {
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
    getApiObjectCoordinates,
    isApartment,
} from '@bpd-library/utilities';

type ShouldDrawPolygonLogicOptions = {
    alwaysShowSoldProjectPolygons?: boolean;
    focusProjectPhase?: { id: string; districtId: string };
};

/**
 * @description Checks if an object of the following types should be drawn on the map.
 * @export
 * @param {(ExtendedConstructionNumber | ExtendedPropertyType | ExtendedProjectPhase)} object
 * @return {boolean}
 */
export default function shouldDrawPolygon(
    object: ExtendedConstructionNumber | ExtendedPropertyType | ExtendedProjectPhase,
    options?: ShouldDrawPolygonLogicOptions,
) {
    switch (object.objectType) {
        case 'constructionNumber':
            return shouldDrawConstructionNumber(object);
        case 'propertyType':
            return shouldDrawPropertyType(object);
        case 'projectPhase':
            return shouldDrawProjectPhase(object, options);
    }
}

function shouldDrawConstructionNumber(constructionNumber: ExtendedConstructionNumber) {
    if (!getApiObjectCoordinates(constructionNumber)) return false;

    return !isApartment({ constructionNumber });
}

function shouldDrawPropertyType(propertyType: ExtendedPropertyType) {
    if (!getApiObjectCoordinates(propertyType)) return false;

    const hasApartmentBuilding = !!propertyType.apartmentBuildings?.length;
    const constructionNumberIsDrawn = !!propertyType.constructionNumbers?.find(
        shouldDrawConstructionNumber,
    );

    return !constructionNumberIsDrawn && !hasApartmentBuilding;
}

function shouldDrawProjectPhase(
    projectPhase: ExtendedProjectPhase,
    options?: ShouldDrawPolygonLogicOptions,
) {
    if (!getApiObjectCoordinates(projectPhase)) return false;
    const showSold = !!options?.alwaysShowSoldProjectPolygons && projectPhase.status === 'Sold';
    if (showSold) return true;

    if (projectPhase.districtId === options?.focusProjectPhase?.districtId) return true;

    const propertyTypeIsDrawn = !!projectPhase.propertyTypes?.find(shouldDrawPropertyType);
    const constructionNumberIsDrawn = !!projectPhase.constructionNumbers?.find(
        shouldDrawConstructionNumber,
    );

    return !propertyTypeIsDrawn && !constructionNumberIsDrawn;
}
